import { HttpErrorResponse, HttpRequest, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  InjectionToken,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core'
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, provideMomentDateAdapter } from '@angular/material-moment-adapter'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { MatIconRegistry } from '@angular/material/icon'
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading } from '@angular/router'
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular'
import {
  artificialDelayInterceptor,
  errorInterceptorFn,
  HTTP_ERROR_INTERCEPTOR_IGNORE_LIST,
  isCustomerFacingStage,
  provideKeinBrowserAutofill,
  provideSharedService,
  provideStageTitle,
  provideWhatsappIcon,
  provideZahlenfelderOhneMausrad,
} from '@saniup-webapps/shared'
import { env } from '../environments/environment'
import { EnvironmentModel } from '../environments/environment.model'
import { appRoutes } from './app.routes'

import { registerLocaleData } from '@angular/common'
import localeDe from '@angular/common/locales/de'
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin'
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin'
import { provideStore, Store, withNgxsDevelopmentOptions } from '@ngxs/store'
import 'moment/locale/de'
import { provideHartmannIcon } from './components/hartmann-ccaas-button/hartmann-icon.provider'
import { HERKUNFT_QUELLE, HerkunftsZeichenService } from './components/mitarbeiter-avatar/herkunfts-zeichen.service'
import { PROFILBILD_QUELLE, ProfilbildService } from './components/mitarbeiter-avatar/profilbild.service'
import { errorMsgInterceptor } from './interceptors/error-msg.interceptor'
import { hausWahlInterceptor } from './haus-wechsel/haus-wahl.interceptor'
import { DashboardStateHimiUpdateColumns } from './store/dashboard/himi-dashboard.state'
import { LogisticDashboardState } from './store/dashboard/logistic-dashboard.state'
import { DashboardStatePatientUpdateColumns } from './store/dashboard/patient-dashboard.state'
import { DashboardStatePhmUpdateColumns } from './store/dashboard/phm-dashboard.state'
import { dashboardStates } from './store/dashboard/states'
import { DokumentenScannerState } from './store/dokumenten-scanner.state'
import { OnboardingState } from './store/onboarding.state'
import { RelativeTimeState } from './store/relative-time.state'
import { IdlePreload } from './util/preload.strategy'
import { ADMIN_REITER } from './admin-page/admin-reiter'

registerLocaleData(localeDe)

export const ENV_TOKEN = new InjectionToken<EnvironmentModel>('app-env')

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENV_TOKEN, useValue: env() },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass('material-symbols-outlined')
    }),
    provideZonelessChangeDetection(),
    // Reicht unbehandelte Promise-Fehler (z. B. ein fehlender Lazy-Chunk nach einem Deploy) an den
    // GlobalErrorHandlerService weiter, der dann neu laedt. Zoneless tut das sonst niemand.
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withPreloading(IdlePreload),
      withComponentInputBinding(),
    ),
    provideAuth0(env().auth),
    provideHttpClient(
      withInterceptors([errorMsgInterceptor, errorInterceptorFn(), hausWahlInterceptor, authHttpInterceptorFn, artificialDelayInterceptor]),
      withFetch(),
    ),
    {
      provide: HTTP_ERROR_INTERCEPTOR_IGNORE_LIST,
      useValue: [
        (req: HttpRequest<unknown>, err: HttpErrorResponse) => {
          return err.status === 409
        },
        (req: HttpRequest<unknown>, err: HttpErrorResponse) => {
          return req.method === 'POST' && err.status === 409 && req.url.endsWith('/api/patients')
        },
        // Ein 404 auf einen HiMi-Entwurf heisst: der Vorgang ist abgeschlossen oder verworfen — der
        // haeufigste Weg dorthin ist die Browser-Zurueck-Taste. Der Assistent behandelt das selbst
        // (`meldeVorgangBeendet`: Dialog mit einem Weg heraus, Autosave still). In der
        // Fehlerzentrale waere es doppelt gemeldet und saehe nach einem Defekt aus — es ist keiner.
        (req: HttpRequest<unknown>, err: HttpErrorResponse) => {
          return err.status === 404 && req.url.includes('/api/himi-versorgung-draft/')
        },
        // Ein fehlendes Profilbild (gerade ersetzt, die Liste ist noch alt) zeigt Initialen. Kein Defekt.
        (req: HttpRequest<unknown>, err: HttpErrorResponse) => {
          return err.status === 404 && req.url.includes('/leistungserbringer/employees/profilbild/')
        },
      ],
    },
    // Der Mitarbeiter-Avatar holt Bilder ueber dieses Token; im Harness fehlt es, dort stehen Initialen.
    { provide: PROFILBILD_QUELLE, useExisting: ProfilbildService },
    // Ebenso die Zeichen von Haus und Partnern (Logo statt Initialen); ohne Token bleiben Initialen.
    { provide: HERKUNFT_QUELLE, useExisting: HerkunftsZeichenService },
    provideMomentDateAdapter({
      parse: {
        dateInput: ['l', 'LL'],
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
      },
    }),
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    // Die Adressen hier sind die von Patient:innen, nicht die der Mitarbeiterin (Begruendung am Provider).
    provideKeinBrowserAutofill(),
    provideZahlenfelderOhneMausrad(),
    provideStageTitle(),
    provideSharedService({
      loginConfig: {
        internalRedirectUri: '/',
        // Reiter „Benutzer:innen" der Admin-Seite (`admin-reiter.ts`). Der Dialog „So bekommen Sie einen Zugang" schickt Nutzer ohne
        // Konto dorthin, dieselbe Adresse wie in `ZugangAusstehendComponent`.
        benutzerverwaltungPfad: `/admin?tab=${ADMIN_REITER.BENUTZER}`,
      },
      stage: env().stage,
    }),
    provideWhatsappIcon(),
    provideHartmannIcon(),
    provideStore(
      [DokumentenScannerState, OnboardingState, LogisticDashboardState, RelativeTimeState, ...dashboardStates],
      { developmentMode: !isCustomerFacingStage(env().stage) },
      withNgxsDevelopmentOptions({ warnOnUnhandledActions: true }),
      withNgxsReduxDevtoolsPlugin({ maxAge: 25, disabled: isCustomerFacingStage(env().stage) }),
      withNgxsStoragePlugin({ keys: [...dashboardStates, OnboardingState, RelativeTimeState] }),
    ),
    provideAppInitializer(() => {
      inject(Store).dispatch([
        new DashboardStatePhmUpdateColumns(),
        new DashboardStatePatientUpdateColumns(),
        new DashboardStateHimiUpdateColumns(),
      ])
    }),
  ],
}
